import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import LoginPage from './LoginPage';
import Callback from './Callback';
import './App.css';
import SurveyPage from './SurveyPage';
import PostSurveyPage from './PostSurveyPage';
import { load, debouncedSaver } from './persist';
import { PROBLEMS, FINAL_PROBLEM_ID } from './problems';
import { SubmissionTaskPage, SubmissionCodePage } from './SubmissionViewPage';


import IdePage from './IdePage'; // Import the new component
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const EXAMPLE_STAGE = 'example';
const MINI_STAGE = 'mini';
const MAIN_STAGE = 'main';
const WORKSPACE_STORAGE_KEYS = [
  'rt_steps_draft',
  'rt_varnames',
  'rt_program_input',
  'rt_program_output',
  'rt_ide_code',
  'rt_chat_histories',
  'rt_active_chat',
  'rt_refinement_step',
  'rt_tests_session_id',
  'rt_refine_session_id',
  'rt_test_run_completed',
  'rt_tests_passed',
  'rt_is_verified_complete',
  'rt_steps',
  'rt_active_ide_problem',
];

const clearWorkspaceState = () => {
  WORKSPACE_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {}
  });
};

function StepsHub() {
  const navigate = useNavigate();
  const [problemStage, setProblemStage] = useState(() => {
    const stage = localStorage.getItem("rt_flow_stage");
    if (stage === MAIN_STAGE || stage === MINI_STAGE || stage === EXAMPLE_STAGE) {
      return stage;
    }
    return EXAMPLE_STAGE;
  });

  useEffect(() => {
    let surveyDone = false;
    try {
      const v = JSON.parse(localStorage.getItem("rt_survey_submitted") || "null");
      surveyDone = !!(v && v.ts);
    } catch {
      surveyDone = !!localStorage.getItem("rt_survey_submitted");
    }
    if (!surveyDone) {
      navigate("/survey", { replace: true });
      return;
    }

    if (localStorage.getItem("rt_post_survey_submitted")) {
      navigate("/thank-you", { replace: true });
      return;
    }

    const stage = localStorage.getItem("rt_flow_stage");
    if (stage === EXAMPLE_STAGE) {
      setProblemStage(EXAMPLE_STAGE);
      return;
    }
    if (stage === "post_survey") {
      navigate("/post-survey", { replace: true });
      return;
    }
    if (stage === "ide") {
      navigate("/ide", { replace: true });
      return;
    }
    if (stage === "done") {
      navigate("/thank-you", { replace: true });
      return;
    }

    if (stage === MAIN_STAGE) {
      setProblemStage(MAIN_STAGE);
      return;
    }
    if (stage === MINI_STAGE) {
      setProblemStage(MINI_STAGE);
      return;
    }
    localStorage.setItem("rt_flow_stage", EXAMPLE_STAGE);
    setProblemStage(EXAMPLE_STAGE);
  }, [navigate]);

  const handleAdvanceProblem = useCallback(() => {
    clearWorkspaceState();
    const nextStage =
      problemStage === EXAMPLE_STAGE ? MINI_STAGE :
      problemStage === MINI_STAGE ? MAIN_STAGE :
      MAIN_STAGE;
    localStorage.setItem("rt_flow_stage", nextStage);
    localStorage.setItem("rt_last_route", "/");
    setProblemStage(nextStage);
    navigate("/", { replace: true });
  }, [navigate, problemStage]);

  const activeProblemId = [EXAMPLE_STAGE, MINI_STAGE, MAIN_STAGE].includes(problemStage)
    ? problemStage
    : MINI_STAGE;
  const problemConfig = PROBLEMS[activeProblemId];

  return (
    <MainApp
      key={activeProblemId}
      problemId={activeProblemId}
      problemConfig={problemConfig}
      isFinalProblem={activeProblemId === FINAL_PROBLEM_ID}
      onAdvanceProblem={activeProblemId === FINAL_PROBLEM_ID ? undefined : handleAdvanceProblem}
    />
  );
}

/* Saves the current path to localStorage whenever it changes */
function PathPersistence() {
  const location = useLocation();
  useEffect(() => {
    try {
      localStorage.setItem('rt_last_route', location.pathname);
    } catch {}
  }, [location.pathname]);
  return null;
}

/* On boot, if we’re at "/" and have steps, jump to /ide; otherwise restore last route */
function StartupRedirect({ isLoggedIn }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) return;

    const path     = window.location.pathname;
    const last     = localStorage.getItem("rt_last_route");
    const stage    = localStorage.getItem("rt_flow_stage");
    const done     = !!localStorage.getItem("rt_post_survey_submitted");

    // Only steer from home route; other routes should be left alone
    if (path === "/") {
      // 1) Fully done → thank-you
      if (done || stage === "done") {
        navigate("/thank-you", { replace: true });
        return;
      }

      // 2) Explicit stage has priority
      if (stage === "post_survey") {
        navigate("/post-survey", { replace: true });
        return;
      }
      if (stage === "ide") {
        navigate("/ide", { replace: true });
        return;
      }

      // 3) Restore last route (e.g., /survey) if we have one
      if (last && last !== "/") {
        navigate(last, { replace: true });
        return;
      }
    }
  }, [isLoggedIn, navigate]);

  // Keep a simple "last route" breadcrumb up to date
  useEffect(() => {
    const writeLast = () => {
      try {
        localStorage.setItem("rt_last_route", window.location.pathname);
      } catch {}
    };
    writeLast();
    window.addEventListener("popstate", writeLast);
    return () => window.removeEventListener("popstate", writeLast);
  }, []);

  return null;
}




// App.js (top-level, outside components)
export async function logEvent(event_type, details = {}) {
  const entry = {
    event_type,
    details: { client_ts: new Date().toISOString(), route: window.location.pathname, ...details }
  };

  // 1) Append locally (never overwrite)
  try {
    const buf = JSON.parse(localStorage.getItem('rt_log_buffer') || '[]');
    buf.push(entry);
    localStorage.setItem('rt_log_buffer', JSON.stringify(buf));
  } catch {}

  // 2) Send to backend (use Beacon when available)
  const body = JSON.stringify(entry);
  const url = '/api/log';
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body });
    }
  } catch {}
}

// A unique ID for new steps
let nextId = 1;

function MainApp({ problemId, problemConfig, isFinalProblem, onAdvanceProblem }) {
const defaultVariables = problemConfig.defaultVariables || ['nums', 'counter', 'tracker'];
const readOnly = !!problemConfig.readOnly;
const prefilledSteps = problemConfig.prefilledSteps || [];
const buildDefaultTraceRow = () => defaultVariables.map(() => ({ value: 'uninitialized' }));
const buildDefaultSteps = () => [{
  id: "0",
  description: "",
  traceVars: [buildDefaultTraceRow()],
  indent: 0,
  isCorrect: null,
  feedback: "",
  incompletePlaceholder: false
}];

const persistProblemState = (suffix, value) => {
  try {
    localStorage.setItem(`rt_${problemId}_${suffix}`, JSON.stringify(value));
  } catch {}
};

const [steps, setSteps] = useState(() => {
  if (readOnly && prefilledSteps.length > 0) {
    return JSON.parse(JSON.stringify(prefilledSteps));
  }
  return load('rt_steps_draft', buildDefaultSteps());
});
// ---------- STATE ----------

// I/O
const [programInput,  setProgramInput]  = useState(() =>
  readOnly ? (problemConfig.defaultInput || "") : load('rt_program_input', problemConfig.defaultInput || "")
);
const [programOutput, setProgramOutput] = useState(() =>
  readOnly ? (problemConfig.defaultOutput || "") : load('rt_program_output', problemConfig.defaultOutput || "")
);

// Chat
const [activeChat, setActiveChat] = useState(() => load('rt_active_chat', null));
const [chatInput, setChatInput]   = useState(""); // ephemeral (not persisted)

// UI (session-only unless you want to persist)
// const [selectedVar, setSelectedVar] = useState(null);
// const [isProblemExpanded, setIsProblemExpanded] = useState(false);
// const [editingVar, setEditingVar] = useState(null);

// Flow flags / session
const [refinementStep, setRefinementStep]     = useState(() => load('rt_refinement_step', 0));
const [testRunCompleted, setTestRunCompleted] = useState(() => load('rt_test_run_completed', false));
const [testsPassed, setTestsPassed]           = useState(() => load('rt_tests_passed', false));
const [isVerifiedComplete, setIsVerifiedComplete] = useState(() => load('rt_is_verified_complete', false));

const [notification, setNotification] = useState({ isVisible: false, message: '', type: '' });
const [feedbackVotes, setFeedbackVotes] = useState({});

// Keep selection for now (persisted)
// const [selectedStepId, setSelectedStepId] = useState(() => load('rt_selected_step_id', null));

// Auth / guards
const [isLoggedIn, setIsLoggedIn] = useState(false);
const [isLoading, setIsLoading]   = useState(true);
const [isConfirmingSubmit, setIsConfirmingSubmit] = useState(false);
const [submitIntent, setSubmitIntent] = useState('advance');

// Variables list
const [variableNames, setVariableNames] = useState(() => load('rt_varnames', defaultVariables));

// Async / sessions
const [isVerifying, setIsVerifying]         = useState(false);
const [testsSessionId, setTestsSessionId]   = useState(() => load('rt_tests_session_id', null));
const [refineSessionId, setRefineSessionId] = useState(() => load('rt_refine_session_id', null));

const [chatHistories, setChatHistories] = useState(() =>
  load('rt_chat_histories', { tests: [], refine: [] })
);


// Refs
const navigate   = useNavigate();


// ---------- DEBOUNCED SAVERS ----------

const savePI    = useRef(debouncedSaver('rt_program_input')).current;
const savePO    = useRef(debouncedSaver('rt_program_output')).current;
const saveAC    = useRef(debouncedSaver('rt_active_chat')).current;
const saveRS    = useRef(debouncedSaver('rt_refinement_step')).current;
const saveTRC   = useRef(debouncedSaver('rt_test_run_completed')).current;
const saveTP    = useRef(debouncedSaver('rt_tests_passed')).current;
const saveIVC   = useRef(debouncedSaver('rt_is_verified_complete')).current;
const saveVN    = useRef(debouncedSaver('rt_varnames')).current;
// const saveSelId = useRef(debouncedSaver('rt_selected_step_id')).current;
const saveTestSID   = useRef(debouncedSaver('rt_tests_session_id')).current;
const saveRefineSID = useRef(debouncedSaver('rt_refine_session_id')).current;
const saveChats = useRef(debouncedSaver('rt_chat_histories')).current;


// ---------- PERSIST EFFECTS ----------

useEffect(() => { if (readOnly) return; savePI(programInput); },        [programInput, savePI, readOnly]);
useEffect(() => { if (readOnly) return; savePO(programOutput); },       [programOutput, savePO, readOnly]);
useEffect(() => { if (readOnly) return; saveAC(activeChat); },          [activeChat, saveAC, readOnly]);
useEffect(() => { if (readOnly) return; saveRS(refinementStep); },      [refinementStep, saveRS, readOnly]);
useEffect(() => { if (readOnly) return; saveTRC(testRunCompleted); },   [testRunCompleted, saveTRC, readOnly]);
useEffect(() => { if (readOnly) return; saveTP(testsPassed); },         [testsPassed, saveTP, readOnly]);
useEffect(() => { if (readOnly) return; saveIVC(isVerifiedComplete); }, [isVerifiedComplete, saveIVC, readOnly]);
useEffect(() => { if (readOnly) return; saveVN(variableNames); },       [variableNames, saveVN, readOnly]);
useEffect(() => { if (readOnly) return; saveTestSID(testsSessionId); }, [testsSessionId, saveTestSID, readOnly]);
useEffect(() => { if (readOnly) return; saveRefineSID(refineSessionId); }, [refineSessionId, saveRefineSID, readOnly]);
useEffect(() => { if (readOnly) return; saveChats(chatHistories); }, [chatHistories, saveChats, readOnly]);

useEffect(() => {
  if (readOnly) return;
  const flush = () => {
    try {
      localStorage.setItem('rt_steps_draft', JSON.stringify(steps));
      localStorage.setItem('rt_varnames', JSON.stringify(variableNames));
      localStorage.setItem('rt_program_input', JSON.stringify(programInput));
      localStorage.setItem('rt_program_output', JSON.stringify(programOutput));
      localStorage.setItem('rt_chat_histories', JSON.stringify(chatHistories));
      localStorage.setItem('rt_active_chat', JSON.stringify(activeChat));
      localStorage.setItem('rt_refinement_step', JSON.stringify(refinementStep));
      localStorage.setItem('rt_tests_session_id', JSON.stringify(testsSessionId));
      localStorage.setItem('rt_refine_session_id', JSON.stringify(refineSessionId));
      localStorage.setItem('rt_test_run_completed', JSON.stringify(testRunCompleted));
      localStorage.setItem('rt_tests_passed', JSON.stringify(testsPassed));
      localStorage.setItem('rt_is_verified_complete', JSON.stringify(isVerifiedComplete));
    } catch {}
  };
  window.addEventListener('beforeunload', flush);
  return () => window.removeEventListener('beforeunload', flush);
}, [
  steps,
  variableNames,
  programInput,
  programOutput,
  chatHistories,
  activeChat,
  refinementStep,
  testsSessionId,
  refineSessionId,
  testRunCompleted,
  testsPassed,
  isVerifiedComplete,
  readOnly
]);


  const createDefaultTraceVars = () => {
    const names = (variableNames && variableNames.length > 0)
      ? variableNames
      : defaultVariables;
    return [names.map(() => ({ value: 'uninitialized' }))];
  };

  const cloneTraceVars = (sourceStep) => {
    if (!sourceStep || !Array.isArray(sourceStep.traceVars) || sourceStep.traceVars.length === 0) {
      return createDefaultTraceVars();
    }
    return JSON.parse(JSON.stringify(sourceStep.traceVars));
  };

  // const ThankYouPage = () => (
  //   <div style={{ textAlign: 'center', padding: '4rem' }}>
  //     <h1>Thank You!</h1>
  //     <p>Your submission has been received.</p>
  //   </div>
  // );

  // useEffect(() => {
  //   fetch('/api/check_session')
  //     .then(res => res.json())
  //     .then(data => {
  //       if (data.logged_in) {
  //         setIsLoggedIn(true);
  //       }
  //     });
  // }, []);


  useEffect(() => {
    if (notification.isVisible) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, isVisible: false });
      }, 4000); // Hide after 4 seconds

      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [notification]);


  // const handleProgramInputChange = (id, value) => {
  //   const newSteps = steps.map(step => 
  //     step.id === id ? { ...step, programInput: value } : step
  //   );
  //   setSteps(newSteps);
  // };



  const problemTitle = problemConfig.title;
  const problemDescription = problemConfig.description;

  const chatBoxRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const box = chatBoxRef.current;
    if (!box) return;

    const header = box.querySelector('.chat-header');
    if (!header) return;

    const handleMouseDown = (e) => {
      offset.current = {
        x: e.clientX - box.getBoundingClientRect().left,
        y: e.clientY - box.getBoundingClientRect().top,
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
      box.style.left = `${e.clientX - offset.current.x}px`;
      box.style.top = `${e.clientY - offset.current.y}px`;
      box.style.position = 'absolute';
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    header.addEventListener('mousedown', handleMouseDown);

    return () => {
      header.removeEventListener('mousedown', handleMouseDown);
    };
  }, [activeChat]);

  const handleDeleteStep = (selectedIndex) => {
    if (readOnly) return;

    if (selectedIndex === 0) {
      logEvent('delete_step_blocked_first_step', { index: selectedIndex, stepId: steps[0]?.id });
      return;
    }

    // Do nothing if no step is selected
    const sourceStep = steps[selectedIndex];
    let deleteCount = 1; // Start with the parent step itself

    // Count how many sub-steps to delete
    for (let i = selectedIndex + 1; i < steps.length; i++) {
      if (steps[i].indent > sourceStep.indent) {
        deleteCount++;
      } else {
        break; // End of the unit
      }
    }

    logEvent('delete_step_click', {
      index: selectedIndex,
      deletedCount: deleteCount,
      stepId: sourceStep.id
    });

    const newSteps = [...steps];
    newSteps.splice(selectedIndex, deleteCount); // Remove the entire block
    setSteps(newSteps);
    // setSelectedStepId(null); // Clear selection
  };

  // Add this new function inside MainApp
  // Add this new function inside MainApp
  const handleVarNameChange = (varIndex, newName) => {
    if (readOnly) return;
    logEvent('varname_change', { varIndex, newName });
    const newNames = [...variableNames];
    newNames[varIndex] = newName;
    setVariableNames(newNames);
  };

  // Add this new function inside MainApp
  const handleSubmit = () => {
    if (readOnly) {
      onAdvanceProblem?.();
      return;
    }
    const intent = isFinalProblem ? 'final' : 'advance';
    setSubmitIntent(intent);
    logEvent('submit_modal_open', { intent });
    setIsConfirmingSubmit(true);
  };

  const executeSubmit = () => {
    const stepsToLog = steps.map(step => ({
      id: step.id ?? "",
      description: step.description ?? "",
      simulation: step.simulation ?? "",
      traceVars: step.traceVars ?? [],   // <-- include traces
      indent: typeof step.indent === 'number' ? step.indent : 0,
      type: step.type ?? 'normal',
    }));

    // Persist per-problem snapshots
    persistProblemState('steps', stepsToLog);
    persistProblemState('varnames', variableNames);
    // Persist for hard refreshes
    localStorage.setItem('rt_steps', JSON.stringify(stepsToLog));
    localStorage.setItem('rt_varnames', JSON.stringify(variableNames));
    logEvent('submit_solution_click', { steps: stepsToLog, problem_stage: problemId });

    if (isFinalProblem) {
      try {
        localStorage.setItem('rt_active_ide_problem', problemId);
        localStorage.setItem('rt_flow_stage', 'ide');
        localStorage.setItem('rt_last_route', '/ide');
      } catch {}
      navigate('/ide', { state: { steps: stepsToLog, variableNames, problemId } });
    } else {
      onAdvanceProblem?.();
    }
  };


  // Add this new function inside MainApp
  const handleTraceVarChange = (stepId, rowIndex, varIndex, newValue) => {
    if (readOnly) return;
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        // Create a deep copy to be safe
        const newTraceVars = JSON.parse(JSON.stringify(step.traceVars));
        // Directly access the value using the row and variable indexes
        newTraceVars[rowIndex][varIndex].value = newValue;
        return { ...step, traceVars: newTraceVars };
      }
      return step;
    });
    setSteps(newSteps);
  };

  // Add this new function inside MainApp
  const addIterationRow = (stepId, rowIndex) => {
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        // Find the source row to copy from
        const sourceRow = step.traceVars[rowIndex];
  
        // Create a deep copy of the source row's values
        const newRow = JSON.parse(JSON.stringify(sourceRow));
  
        const newTraceVars = [...step.traceVars];
        // Splice the new, copied row in right after the current row
        newTraceVars.splice(rowIndex + 1, 0, newRow);

        return { ...step, traceVars: newTraceVars };
      }
      return step;
    });
    setSteps(newSteps);
    logEvent('iteration_row_add', { stepId, afterRowIndex: rowIndex });
  };

  // Add this new function inside MainApp
  // Replace the old handleAddSubStep with this one
  const handleAddSubStep = (parentIndex) => {
    if (readOnly) return;
    if (parentIndex == null || parentIndex < 0 || parentIndex >= steps.length) return;
  
    const parentStep = steps[parentIndex];
  
    const newSubStep = {
      id: (nextId++).toString(),
      description: "",
      isCorrect: null,
      feedback: "",
      indent: (parentStep?.indent ?? 0) + 1,  // one level deeper than parent
      type: "iteration",
      credentials: "include",
      traceVars: cloneTraceVars(parentStep),
      incompletePlaceholder: false
    };
  
    logEvent("add_substep_click", { parentId: parentStep.id, newStepId: newSubStep.id });
  
    const newSteps = [...steps];
    // Insert immediately after the parent, BEFORE any existing children
    newSteps.splice(parentIndex + 1, 0, newSubStep);
    setSteps(newSteps);
  };

  // Add this new function inside MainApp
  const handleOnDragEnd = (result) => {
    if (readOnly) return;
    // Do nothing if the item was dropped outside the list
    if (!result.destination) return;
    logEvent('steps_reorder', {
      sourceIndex: result.source.index,
      destIndex: result.destination.index,
      draggableId: result.draggableId
    });

    const items = Array.from(steps);
    // Remove the dragged item from its original position
    const [reorderedItem] = items.splice(result.source.index, 1);
    // Insert the dragged item into its new position
    items.splice(result.destination.index, 0, reorderedItem);

    setSteps(items);
  };

  // Add these new functions inside MainApp
  const handleIndent = (stepIndex) => {
    if (readOnly) return;

    const newSteps = [...steps];
    const currentStep = newSteps[stepIndex];

    // Rule: Can't indent the very first step.
    if (stepIndex === 0) return;

    const prevStep = newSteps[stepIndex - 1];
    // Rule: Can only indent one level deeper than the step above.
    if (currentStep.indent >= prevStep.indent + 1) return;

    currentStep.indent += 1;
    setSteps(newSteps);
    logEvent('indent_click', { stepId: currentStep.id, oldIndent: currentStep.indent - 1, newIndent: currentStep.indent });

  };

  const handleOutdent = (stepIndex) => {
    if (readOnly) return;
    const newSteps = [...steps];
    const currentStep = newSteps[stepIndex];

    // Rule: Can't outdent if already at the top level.
    if (currentStep.indent === 0) return;

    currentStep.indent -= 1;
    setSteps(newSteps);
    logEvent('outdent_click', { stepId: currentStep.id, oldIndent: currentStep.indent + 1, newIndent: currentStep.indent });

  };

  // Replace the old handleAddStep with this one
  const handleAddStep = (afterIndex) => {
    if (readOnly) return;
    const prevStep = steps[afterIndex];
  
    const newStep = {
      id: (nextId++).toString(),
      description: "",
      isCorrect: null,
      feedback: "",
      indent: prevStep?.indent ?? 0,        // same level as the clicked step
      traceVars: cloneTraceVars(prevStep),  // copy trace vars from the clicked step
      credentials: 'include',
      type: 'normal',
      incompletePlaceholder: false
    };
  
    logEvent('add_step_click', { afterIndex, newStepId: newStep.id });
  
    const newSteps = [...steps];
    newSteps.splice(afterIndex + 1, 0, newStep);   // insert right after the clicked step
    setSteps(newSteps);
  };
  

  const handleDescriptionChange = (id, value) => {
    if (readOnly) return;
    const trimmed = value.trim();
    const newSteps = steps.map(step =>
      step.id === id
        ? {
            ...step,
            description: value,
            incompletePlaceholder: trimmed.length > 0 ? false : step.incompletePlaceholder
          }
        : step
    );
    setSteps(newSteps);
  };

  // const handleSimulationChange = (id, value) => {
  //   if (readOnly) return;
  //   const newSteps = steps.map(step =>
  //     step.id === id ? { ...step, simulation: value } : step
  //   );
  //   setSteps(newSteps);
  // };

  // In App.js, add this new handler function
  // const handleDisabledRefineClick = (e) => { ... };
  // const handleDisabledTestClick = (e) => { ... };

  const logEvent = async (eventType, details = {}) => {
    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: eventType,
          details: {
            client_ts: new Date().toISOString(),
            route: window.location.pathname,
            problem_id: problemId,
            ...details
          }
        })
      });
    } catch (error) {
      console.error("Logging to backend failed:", error);
    }
  };

  const handleChatClose = () => {
    const type = activeChat;
    const history = chatHistories[type] ?? [];
    logEvent('chat_close_click', {
      chatType: type,
      refinementStep,
      history,
      client_ts: new Date().toISOString(),
    });
    setActiveChat(null);
  };



  const handleVerify = async () => {
    if (readOnly) return;
    logEvent('verify_click');
    setIsVerifying(true); // <-- Start loading
    const cancelSlow = startSlowNotice(
      'The system is taking longer to verify. Retrying…',
      10000 // 10s
      );
    // 0. CLEAR ALL HIGHLIGHTS: Before making the API call, reset all step statuses.
    // This provides immediate visual feedback that a new verification is running.
    const resetSteps = steps.map(step => ({ ...step, isCorrect: null, feedback: "" }));
    setSteps(resetSteps);

    // 1. Check if there are no steps at all.
    if (resetSteps.length === 0) {
      const templateTrace = cloneTraceVars(steps[steps.length - 1]);
      const newIncompleteStep = {
        id: nextId++,
        description: "Your solution is not complete.",
        isCorrect: false,
        feedback: "Please add at least one step before verifying.",
        traceVars: templateTrace,
        credentials: 'include',
        incompletePlaceholder: false
      };
      setSteps([newIncompleteStep]);
      cancelSlow(); 
      return; // Stop before making an API call
    }

    // 2. Check if any step description is empty.
    const firstEmptyStepIndex = steps.findIndex(step => step.description.trim() === '');
    if (firstEmptyStepIndex !== -1) {
      // If an empty step is found, update its description and mark it red.
      setSteps(prevSteps => prevSteps.map((step, index) =>
        index === firstEmptyStepIndex
          ? { ...step, isCorrect: false, feedback: "This step cannot be empty." }
          : step
      ));
      cancelSlow(); 
      return; // Stop execution
    }

    // Prepare the data using the cleaned steps
    const verificationData = {
      problem: problemDescription,
      program_input: programInput,
      variable_names: variableNames, // <-- Add the global variable names
      steps: resetSteps.map(step => ({
        description: step.description,
        trace: step.traceVars // <-- Send the full trace data
      })),
      problem_id: problemId,
    };

    try {
      const response = await fetch('/api/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verificationData),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Verification request failed.');

      const result = await response.json();
      logEvent('verify_result', result);

      setTestsPassed(false);
      setIsVerifiedComplete(false);

      // 3. Handle the verification result
      switch (result.status) {
        case 'incorrect':
          const incorrectStepIndex = result.step_number - 1;
          const incorrectReason = result.explanation || "Revise this step based on the feedback.";
          setSteps(prevSteps => prevSteps.map((step, index) => {
            if (index < incorrectStepIndex) return { ...step, isCorrect: true, feedback: "" };
            if (index === incorrectStepIndex) return { ...step, isCorrect: false, feedback: incorrectReason };
            return { ...step, isCorrect: null, feedback: "" };
          }));
          break;

        case 'complete':
          setIsVerifiedComplete(true);
          setSteps(prevSteps => prevSteps.map(step => ({ ...step, isCorrect: true, feedback: "" })));
          setNotification({
            isVisible: true,
            message: result.explanation, // Use the explanation from the AI
            type: 'success'              // Set the type to 'success'
          });
          break;
        case 'incomplete': {

          // Append a blank step the user can fill in next, with the LLM guidance highlighted.
          const templateTrace = cloneTraceVars(steps[steps.length - 1]);
          const newIncompleteStep = {
            id: nextId++,
            description: "",
            simulation: "",
            isCorrect: false,
            feedback: result.explanation || "Finish describing the remaining logic.",
            traceVars: templateTrace,
            incompletePlaceholder: true
          };
          setSteps(prevSteps => [
            ...prevSteps.map(step => ({ ...step, isCorrect: true, feedback: "" })),
            newIncompleteStep
          ]);
          break;
        }

        default:
          console.error("Unknown verification status:", result.status);
      }
    } catch (error) {
      logEvent('verify_error', { error: String(error) });
      console.error("Verification failed:", error);
    } finally {
      cancelSlow();
      setIsVerifying(false); // <-- Stop loading
    }
  };

  const handleChatSend = () => {
    if (readOnly) return;
    const userMsg = chatInput.trim();
    if (!userMsg || !activeChat) return;

    logEvent('chat_send_click', { chatType: activeChat, message: userMsg });

    const currentChatHistory = chatHistories[activeChat] ?? [];

    // --- Refine chat (unchanged, with a tiny safety on lastAiQuestion) ---
    if (activeChat === 'refine') {
      const currentStep = steps[refinementStep];
      const lastBotMsg =
        [...currentChatHistory].reverse().find(m => m.from === "bot")?.text
        || "Could you clarify this step?";

      const historyForUi = [...currentChatHistory, { from: "user", text: userMsg }];
      setChatHistories(prev => ({ ...prev, refine: historyForUi }));
      setChatInput("");

      fetch('/api/refine/continue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problem: problemDescription,
          step: {
            description: currentStep.description,
            traceVars: currentStep.traceVars,
            index: refinementStep,        // optional, helps query
          },
          variable_names: variableNames,
          student_reply: userMsg,
          last_ai_question: lastBotMsg,
          session_id: refineSessionId
        })
      })
        .then(r => r.ok ? r.json() : Promise.reject('Network error'))
        .then(result => {
          // ⬇⬇⬇ Put your success log right here
          logEvent('refine_followup', {
            stepIndex: refinementStep,
            student_reply: userMsg,
            bot_message: result.message
          });

          if (result.done) {
            logEvent('refine_step_complete', { stepIndex: refinementStep });
          }

          setChatHistories(prev => ({
            ...prev,
            refine: [...historyForUi, { from: "bot", text: result.message }]
          }));
          // If result.done === true, you can decide to auto-advance here if you want
        })
        .catch(err => {
          // ⬇⬇⬇ And your error log here
          logEvent('refine_followup_error', {
            stepIndex: refinementStep,
            student_reply: userMsg,
            error: String(err)
          });

          setChatHistories(prev => ({
            ...prev,
            refine: [...historyForUi, { from: "bot", text: "Thanks—that clears it up. Let’s move on." }]
          }));
        });

      return;
    }



    // --- Tests chat: send replies to /api/tests/continue ---
    if (activeChat === 'tests') {
      const historyForUi = [...currentChatHistory, { from: "user", text: userMsg }];
      setChatHistories(prev => ({ ...prev, tests: historyForUi }));
      setChatInput("");

      fetch('/api/tests/continue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problem: problemDescription,
          steps: steps.map(s => ({ description: s.description })), // keep payload lean
          student_reply: userMsg,
          session_id: testsSessionId
        })
      })
        .then(r => r.ok ? r.json() : Promise.reject('Network error'))
        .then(result => {
          // ✅ YOUR SUCCESS LOG GOES HERE
          logEvent('tests_followup', {
            student_reply: userMsg,
            resolved: !!result.resolved,
            bot_message: result.message,
            client_ts: new Date().toISOString()
            
          });

          setChatHistories(prev => ({
            ...prev,
            tests: [...historyForUi, { from: "bot", text: result.message }]
          }));

          if (result.resolved) {
            setTestsPassed(true);
          } else {
            setTestsPassed(false);
          }
        })
        .catch(err => {
          // ❌ YOUR ERROR LOG GOES HERE
          logEvent('tests_followup_error', {
            student_reply: userMsg,
            error: String(err),
            client_ts: new Date().toISOString()
          });

          setChatHistories(prev => ({
            ...prev,
            tests: [...historyForUi, { from: "bot", text: "Sorry—couldn't process that right now." }]
          }));
        });

      return;
    }


    // --- Generic chat fallback (unchanged) ---
    const updatedHistory = [...currentChatHistory, { from: "user", text: userMsg }];
    setChatHistories(prev => ({ ...prev, [activeChat]: updatedHistory }));
    setChatInput("");

    // --- Generic chat fallback ---
    fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: updatedHistory }),
      credentials: 'include'
    })
      .then(r => r.ok ? r.json() : Promise.reject('Network response was not ok.'))
      .then(data => {
        const botText = data.system ?? '';
        logEvent('chat_reply', {
          chatType: activeChat,
          user_msg: userMsg,
          bot_message: botText
        });
        if (botText) {
          setChatHistories(prev => ({
            ...prev,
            [activeChat]: [...updatedHistory, { from: "bot", text: botText }]
          }));
        }
      })
      .catch(err => {
        logEvent('chat_reply_error', {
          chatType: activeChat,
          user_msg: userMsg,
          error: String(err)
        });
        console.error("Chat send failed:", err);
      });

  };


  // Add this new function inside MainApp
  const removeIterationRow = (stepId, rowIndex) => {
    if (readOnly) return;
    const newSteps = steps.map(step => {
      if (step.id === stepId) {
        // Prevent removing the very last row
        if (step.traceVars.length <= 1) return step;

        const newTraceVars = [...step.traceVars];
        newTraceVars.splice(rowIndex, 1);
        return { ...step, traceVars: newTraceVars };
      }
      return step;
    });
    setSteps(newSteps);
    logEvent('iteration_row_remove', { stepId, rowIndex });

  };

  const handleDismissFeedback = (stepId) => {
    logEvent('step_feedback_dismiss', { stepId });
    setSteps(prevSteps => prevSteps.map(step =>
      step.id === stepId ? { ...step, isCorrect: null, feedback: "" } : step
    ));
    setFeedbackVotes(prev => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
  };

  const handleFeedbackVote = (stepId, vote) => {
    const feedbackText = steps.find(step => step.id === stepId)?.feedback || "";
    setFeedbackVotes(prev => {
      const next = { ...prev, [stepId]: vote };
      return next;
    });
    logEvent('step_feedback_vote', { stepId, vote, feedback: feedbackText });
  };

  // Add this new function inside your MainApp component
  // const handleStepSelect = (e, stepId) => {
  //   e.stopPropagation();
  //   setSelectedStepId(prevId => {
  //     const selected = prevId === stepId ? null : stepId;
  //     logEvent('step_select', { stepId, selected: selected !== null });
  //     return selected;
  //   });
  // };


  const handleTests = () => {
    if (readOnly) return;
    logEvent('tests_click');
    setTestRunCompleted(false); // Reset the status at the beginning of a run

    const userPrompt = "Please verify if my solution passes all the test cases.";
    const initialHistory = [
      { from: "user", text: userPrompt },
      { from: "bot", text: "Running tests..." }
    ];

    setChatHistories(prev => ({ ...prev, tests: initialHistory }));
    setActiveChat("tests");
    logEvent('chat_open', { chatType: 'tests' });


    // 3. Prepare and send the data to the backend
    const testData = {
      problem: problemDescription,
      steps: steps.map(s => ({
        description: s.description,
        simulation: s.simulation
      })),
    };

    fetch('/api/tests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(testData),
      credentials: 'include'
    })
      .then(response => response.ok ? response.json() : Promise.reject('Network error'))
      .then(result => {
        if (result.session_id) {
          setTestsSessionId(result.session_id);
        }
        logEvent('tests_result', {
          session_id: result.session_id,
          resolved: !!result.all_passed || !!result.resolved,
          message: result.message
        });
        if (result.all_passed) {
          setTestsPassed(true);
        } else {
          setTestsPassed(false);
        }
        // 4. Replace "Running tests..." with the final result from the backend
        setChatHistories(prev => ({
          ...prev,
          tests: [
            { from: "user", text: userPrompt }, // Keep the user prompt
            { from: "bot", text: result.message }  // Add the final result
          ]
        }));
        setTestRunCompleted(true);
      })
      .catch(error => {
        logEvent('tests_initial_error', { error: String(error) });
        console.error("Testing failed:", error);
        // Handle errors by updating the chat
        setChatHistories(prev => ({
          ...prev,
          tests: [
            { from: "user", text: userPrompt },
            { from: "bot", text: "Sorry, an error occurred while running tests." }
          ]
        }));
        setTestRunCompleted(true);
      });
  };

  // Add this new function inside MainApp
  const handleDuplicateStep = (selectedIndex) => {


    const stepsToDuplicate = [];
    const sourceStep = steps[selectedIndex];
    stepsToDuplicate.push(sourceStep);

    // Find all sub-steps belonging to the source step
    for (let i = selectedIndex + 1; i < steps.length; i++) {
      if (steps[i].indent > sourceStep.indent) {
        stepsToDuplicate.push(steps[i]);
      } else {
        break; // End of the unit
      }
    }

    logEvent('duplicate_step_click', {
      sourceIndex: selectedIndex,
      duplicatedCount: stepsToDuplicate.length
    });

    // Create deep copies with new, unique IDs
    const duplicatedSteps = JSON.parse(JSON.stringify(stepsToDuplicate)).map(step => ({
      ...step,
      id: (nextId++).toString(),
      isCorrect: null,
      feedback: "",
      incompletePlaceholder: false
    }));

    const newSteps = [...steps];
    // Insert the duplicated block after the original block
    newSteps.splice(selectedIndex + stepsToDuplicate.length, 0, ...duplicatedSteps);
    setSteps(newSteps);
  };

  const startSlowNotice = (msg, delayMs = 10000) => {
    const id = setTimeout(() => {
      setNotification({ isVisible: true, message: msg, type: 'info' });
      // optional: log it
      logEvent('slow_operation_notice', { message: msg });
    }, delayMs);
    return () => clearTimeout(id);
  };

  const handleRefine = () => {
    if (readOnly) return;
    const ts = new Date().toISOString();
    logEvent('refine_click', { client_ts: ts });

    // Guard: prerequisites
    if (!testsPassed || !isVerifiedComplete) {
      setNotification({
        isVisible: true,
        message: 'You must pass all tests and have a complete, verified solution first.',
        type: 'warning'
      });
      logEvent('refine_click_blocked', {
        reason: 'prerequisites_not_met',
        testsPassed,
        isVerifiedComplete,
        client_ts: new Date().toISOString(),
      });
      return;
    }

    // Guard: need steps
    if (steps.length === 0) {
      alert("Please add at least one step to refine.");
      logEvent('refine_click_no_steps', { client_ts: new Date().toISOString() });
      return;
    }

    // Start from step 0
    setRefinementStep(0);
    const stepObj = steps[0];

    // Seed UI history
    const historyForUi = [
      { from: "bot", text: "Okay, I'm ready to learn! Let's go through it together." },
      { from: "user", text: `My first step is: "${stepObj.description}"` },
    ];
    setChatHistories(prev => ({ ...prev, refine: historyForUi }));
    setActiveChat("refine");

    // Log chat + step opened
    logEvent('chat_open', {
      chatType: 'refine',
      stepIndex: 0,
      client_ts: new Date().toISOString(),
    });
    logEvent('refine_step_open', {
      stepIndex: 0,
      client_ts: new Date().toISOString(),
    });

    // Ask backend for the FIRST clarifying question for this step
    fetch('/api/refine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        problem: problemDescription,
        step: {
          description: stepObj.description,
          traceVars: stepObj.traceVars
        },
        variable_names: variableNames
      })
    })
      .then(r => (r.ok ? r.json() : Promise.reject('Network error')))
      .then(data => {
        if (data.session_id) {
          setRefineSessionId(data.session_id);
        }
        const botText =
          data.message ??
          data.refined_explanation ??
          "Let’s start by clarifying the key idea—what condition are you checking first here?";

        logEvent('refine_first_question', {
          stepIndex: 0,
          session_id: data.session_id,
          bot_message: botText,
          client_ts: new Date().toISOString(),
        });

        setChatHistories(prev => ({
          ...prev,
          refine: [...historyForUi, { from: "bot", text: botText }]
        }));
      })
      .catch(err => {
        const fallback = "Let’s start by clarifying the key idea—what condition are you checking first here?";

        logEvent('refine_first_question_error', {
          stepIndex: 0,
          error: String(err),
          client_ts: new Date().toISOString(),
        });

        setChatHistories(prev => ({
          ...prev,
          refine: [...historyForUi, { from: "bot", text: fallback }]
        }));
      });
  };



  const handleRefineNext = () => {
    if (readOnly) return;
    logEvent('refine_next_click', { client_ts: new Date().toISOString() });

    const nextStepIndex = refinementStep + 1;

    if (nextStepIndex >= steps.length) {
      // Friendly wrap-up when there are no more steps
      setChatHistories(prev => ({
        ...prev,
        refine: [...(prev.refine ?? []), { from: "bot", text: "All steps reviewed—nice work!" }]
      }));
      logEvent('refine_done', { client_ts: new Date().toISOString() });
      return;
    }

    const stepObj = steps[nextStepIndex];
    const currentHistory = chatHistories.refine ?? [];
    const studentMsg = `Okay, my next step is: "${stepObj.description}"`;

    // Show user's “next step” message in the chat
    const updatedHistoryForUi = [
      ...currentHistory,
      { from: "user", text: studentMsg }
    ];
    setChatHistories(prev => ({ ...prev, refine: updatedHistoryForUi }));

    // Move the pointer
    setRefinementStep(nextStepIndex);
    logEvent('refine_step_open', {
      stepIndex: nextStepIndex,
      client_ts: new Date().toISOString(),
    });

    // Ask backend for the FIRST clarifying question for this next step
    fetch('/api/refine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        problem: problemDescription,
        step: {
          description: stepObj.description,
          traceVars: stepObj.traceVars
        },
        variable_names: variableNames
      })
    })
      .then(r => (r.ok ? r.json() : Promise.reject('Network error')))
      .then(data => {
        const botText =
          data.message ??
          data.refined_explanation ??
          "Got it—could you clarify the decision rule in this step?";

        logEvent('refine_first_question', {
          stepIndex: nextStepIndex,
          student_msg: studentMsg,
          bot_message: botText,
          client_ts: new Date().toISOString(),
        });

        setChatHistories(prev => ({
          ...prev,
          refine: [...updatedHistoryForUi, { from: "bot", text: botText }]
        }));
      })
      .catch(err => {
        const fallback = "Got it—could you clarify the decision rule in this step?";
        logEvent('refine_first_question_error', {
          stepIndex: nextStepIndex,
          student_msg: studentMsg,
          error: String(err),
          client_ts: new Date().toISOString(),
        });
        setChatHistories(prev => ({
          ...prev,
          refine: [...updatedHistoryForUi, { from: "bot", text: fallback }]
        }));
      });
  };




  return (
    <>
      {notification.isVisible && (
        <div className={`notification-banner ${notification.type}`}>
          {notification.message}
          <button
            className="notification-close-btn"
            onClick={() => setNotification({ ...notification, isVisible: false })}
          >
            ×
          </button>
        </div>
      )}

{isVerifying && (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    )}

      <div className="app-container">
        <header>
          <h1>RunThrough</h1>
          <div className="problem-title">
            <h3>{problemTitle}</h3>
            <p>{problemDescription}</p>
          </div>
        </header>
        <DragDropContext onDragEnd={readOnly ? () => {} : handleOnDragEnd}>
          <main>
            <div className="main-program-input-container">
              <label className="program-input-label">Input</label>
              <textarea
                className="program-input-box"
                value={programInput}
                onChange={(e) => { if (!readOnly) setProgramInput(e.target.value); }}
                onBlur={(e) => { if (!readOnly) logEvent('program_input_edit', { value: e.target.value }); }}
                rows={1}
                spellCheck={false}
                readOnly={readOnly}
              />
            </div>
            {/* // Inside the return statement of your MainApp component */}

            {/* // Inside the return statement of MainApp */}
            <Droppable droppableId="steps">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {steps.map((step, index) => (
                    <Draggable
                      key={step.id}
                      draggableId={step.id}
                      index={index}
                      isDragDisabled={readOnly}
                    >
                      {(provided) => {
                        const traceRows = Array.isArray(step.traceVars) && step.traceVars.length > 0
                          ? step.traceVars
                          : createDefaultTraceVars();
                        // 1. This outer wrapper is for positioning the button and receiving D&D props.
                        return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="draggable-wrapper"
                        >
                          {/* 2. The visible row gets the drag handle. */}
                          <div className="step-row" {...provided.dragHandleProps}>
                            <div className="indent-spacer" style={{ width: `${step.indent * 50}px` }}>
                              {/* This indicator will only appear for indented steps */}
                              {step.indent > 0 && (
                                <div className="substep-indicator">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 4V14C10 14.5304 10.2107 15.0391 10.5858 15.4142C10.9609 15.7893 11.4696 16 12 16H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M15 11L20 16L15 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div
                              className={`step-container ${/* selectedStepId === step.id ? 'selected' : '' */ ''}
                              ${step.isCorrect === true ? 'correct' : ''}
                              ${step.isCorrect === false ? 'incorrect' : ''}`}
                              // onClick={(e) => handleStepSelect(e, step.id)}
                            >
                              <div className="description-wrapper">
                                {(() => {
                                  const basePlaceholder = index === 0
                                    ? "Enter your first step here..."
                                    : "Describe your step in natural language...";
                                  const isBlank = !step.description || step.description.trim().length === 0;
                                  const placeholderText =
                                    step.incompletePlaceholder && isBlank
                                      ? "Your logic is incomplete. Describe the missing step here..."
                                      : basePlaceholder;
                                  return (
                                <textarea
                                  className="description-box"
                                  placeholder={placeholderText}
                                  value={step.description}
                                  onChange={(e) => handleDescriptionChange(step.id, e.target.value)}
                                  onBlur={(e) => { if (!readOnly) logEvent('description_edit', { stepId: step.id, value: e.target.value }); }}
                                  spellCheck={false}
                                  readOnly={readOnly}
                                />
                                  );
                                })()}
                                <div className="trace-area">
                                  {/* This is the new, unified rendering logic */}
                                  <div className="trace-vars-container">
                                    <div className="iteration-rows-container">
                                      {traceRows.map((row, rowIndex) => (
                                        <div key={rowIndex} className="iteration-row-wrapper">
                                          <div className="remove-iteration-wrapper">
                                            {/* Show the minus button if it's not the only row */}
                                            {!readOnly && step.traceVars.length > 1 && (
                                              <button onClick={() => removeIterationRow(step.id, rowIndex)} className="remove-iteration-btn"
                                                data-tooltip="Remove iteration row">-</button>
                                            )}
                                          </div>
                                          <div className="iteration-row">
                                            {row.map((variable, varIndex) => (
                                              <div className="trace-var-tag">
                                                <span className="tooltip-wrap" data-tooltip="Change variable name">
                                                  <input
                                                    type="text"
                                                    className="trace-var-name-input"
                                                    value={variableNames[varIndex]}
                                                    onChange={(e) => handleVarNameChange(varIndex, e.target.value)}
                                                    spellCheck={false}
                                                    readOnly={readOnly}
                                                  />
                                                </span>


                                                <span className="tooltip-wrap" data-tooltip="Change variable value">
                                                  <input
                                                    type="text"
                                                    className="trace-var-input"
                                                    value={variable.value}
                                                    onChange={(e) => handleTraceVarChange(step.id, rowIndex, varIndex, e.target.value)}
                                                    onBlur={(e) => { if (!readOnly) logEvent('trace_value_edit', { stepId: step.id, rowIndex, varIndex, value: e.target.value }); }}
                                                    size={Math.max(15, variable.value.length)}
                                                    spellCheck={false}
                                                    readOnly={readOnly}
                                                  />
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                          {!readOnly && (
                                            <div className="add-iteration-wrapper">
                                              <button onClick={() => addIterationRow(step.id, rowIndex)} className="add-iteration-btn"
                                                data-tooltip="Add iteration row">+</button>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                {step.isCorrect === false && step.feedback && (
                                  <div className="step-feedback-banner" role="alert">
                                    <span className="step-feedback-text">{step.feedback}</span>
                                    <div className="feedback-actions">
                                      <button
                                        type="button"
                                        className={`feedback-vote-btn ${feedbackVotes[step.id] === 'up' ? 'selected up' : ''}`}
                                        aria-label="Feedback was helpful"
                                        aria-pressed={feedbackVotes[step.id] === 'up'}
                                        onClick={() => handleFeedbackVote(step.id, 'up')}
                                      >
                                        👍
                                      </button>
                                      <button
                                        type="button"
                                        className={`feedback-vote-btn ${feedbackVotes[step.id] === 'down' ? 'selected down' : ''}`}
                                        aria-label="Feedback was not helpful"
                                        aria-pressed={feedbackVotes[step.id] === 'down'}
                                        onClick={() => handleFeedbackVote(step.id, 'down')}
                                      >
                                        👎
                                      </button>
                                      <button
                                        type="button"
                                        className="feedback-dismiss-btn"
                                        aria-label="Dismiss feedback"
                                        onClick={() => handleDismissFeedback(step.id)}
                                      >
                                        X
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 3. The "Add Step" icon button is a sibling to the step-row for correct positioning. */}
                          {!readOnly && (
                          <div className="step-action-icons">
                            <button onClick={() => handleAddStep(index)} className="add-step-icon-btn" data-tooltip="Add Step">
                              <svg width="24" height="24" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                                <rect x="10" y="10" width="80" height="80" rx="20" ry="20" fill="white" stroke="currentColor" strokeWidth="8" />
                                <path d="M50 30 V 70 M 30 50 H 70" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
                              </svg>
                            </button>
                            <button onClick={() => handleDuplicateStep(index)} className="duplicate-step-icon-btn" data-tooltip="Duplicate">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.5 3H5C3.89543 3 3 3.89543 3 5V15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 7H19C20.1046 7 21 7.89543 21 9V19C21 20.1046 20.1046 21 19 21H9C7.89543 21 7 20.1046 7 19V9C7 7.89543 7.89543 7 9 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button onClick={() => handleAddSubStep(index)} className="add-substep-icon-btn" data-tooltip="Add Sub-step">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 10L20 15L15 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4 4V11C4 12.0609 4.42143 13.0783 5.17157 13.8284C5.92172 14.5786 6.93913 15 8 15H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>

                            <button
                              onClick={() => handleOutdent(index)}
                              className="outdent-step-icon-btn"
                              disabled={steps[index].indent === 0}
                              data-tooltip="Outdent"
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 12H15M3 12L7 8M3 12L7 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /><path d="M21 6H11" stroke="currentColor" stroke-width="2" stroke-linecap="round" /><path d="M21 18H11" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
                            </button>
                            <button
                              onClick={() => handleIndent(index)}
                              className="indent-step-icon-btn"
                              disabled={index === 0 || steps[index].indent >= steps[index - 1].indent + 1}
                              data-tooltip="Indent"
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 12H9M21 12L17 8M21 12L17 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /><path d="M3 6H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" /><path d="M3 18H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
                            </button>
                            <button onClick={() => handleDeleteStep(index)} className="delete-step-icon-btn" data-tooltip={index === 0 ? "First step can’t be deleted" : "Delete"} disabled={index === 0}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
       xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 
             8.58579 2.58579C8.96086 2.21071 
             9.46957 2 10 2H14C14.5304 2 
             15.0391 2.21071 15.4142 2.58579C15.7893 
             2.96086 16 3.46957 16 4V6M19 6V20C19 
             20.5304 18.7893 21.0391 18.4142 
             21.4142C18.0391 21.7893 17.5304 
             22 17 22H7C6.46957 22 5.96086 21.7893 
             5.58579 21.4142C5.21071 21.0391 
             5 20.5304 5 20V6H19Z" 
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
                            </button>


                          </div>
                          )}
                        </div>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {/* --- Main Actions (top of page) --- */}
            {!readOnly && (
              <div className="sticky-actions-bar">
                <div className="actions">
                  <div className="action-group-left">
                    {/* buttons hidden for now */}
                  </div>
                </div>
              </div>
            )}

            {/* --- New Footer Actions (bottom of page) --- */}
            {!readOnly ? (
            <div className="footer-actions">
              {/* An empty spacer for the left side */}
              <div className="footer-spacer"></div>

              {/* A wrapper for the center buttons */}
              <div className="footer-center-group">
                <button onClick={handleVerify} className="verify-btn"
                  data-tooltip="Verify logic for given current input">Verify</button>
                {/* Action buttons temporarily hidden */}
              </div>

              {/* A wrapper for the right-aligned submit button */}
              <div className="footer-right-group">
                <button onClick={handleSubmit} className="submit-btn">Submit</button>
              </div>
              <div className="disclaimer-footer">
              This tool uses LLMs and may make mistakes.
    </div>
            </div>
            ) : null}
            {!readOnly && activeChat && (
              <div className="chat-box" ref={chatBoxRef}>
                <div className="chat-header">
                  {activeChat === "tests" ? "Run Tests" : "Refine Step"}
                  <button className="close-chat" onClick={handleChatClose} type="button">×</button>

                </div>

                {/* The "Next Step" button is now here, right after the header */}
                {activeChat === 'refine' && (
                  <button
                    className="chat-next-btn"
                    onClick={handleRefineNext}
                    disabled={refinementStep >= steps.length - 1}
                  >
                    Next Step
                  </button>
                )}
                {activeChat === 'tests' && testRunCompleted && (
                  <button className="chat-retry-btn" onClick={() => { logEvent('tests_retry_click'); handleTests(); }}>
                    Retry
                  </button>
                )}

                <div className="chat-messages">
                  {chatHistories[activeChat]?.map((msg, idx) => (
                    <div key={idx} className={`chat-message ${msg.from}`}>
                      <span className="chat-message-content">
                        <span className="chat-message-text">{msg.text}</span>
                      </span>
                    </div>
                  ))}
                </div>

                {/* The input container no longer holds the "Next Step" button */}
                <div className="chat-input-container">

                  <input
                    className="chat-input"
                    placeholder="Enter message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    spellCheck={false}
                  />
                  <button className="chat-send" onClick={handleChatSend}>📨</button>
                </div>
              </div>
            )}
            <div className="main-program-output-container">
              <label className="program-output-label">Output</label>
              <textarea
                className="program-output-box"
                value={programOutput}
                onChange={(e) => { if (!readOnly) setProgramOutput(e.target.value); }}
                onBlur={(e) => { if (!readOnly) logEvent('program_output_edit', { value: e.target.value }); }}
                rows={1}
                placeholder="Expected output for the given input..."
                spellCheck={false}
                readOnly={readOnly}
              />
            </div>

            {readOnly && (
              <div className="example-footer">
                <button onClick={() => onAdvanceProblem?.()} className="example-continue-btn">
                  Start Warm-up Task
                </button>
              </div>
            )}
            
          </main>
        </DragDropContext>
      </div>
      {!readOnly && isConfirmingSubmit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{submitIntent === 'final' ? 'Ready to move into the IDE?' : 'Ready for the next question?'}</h3>
            <p>
              {submitIntent === 'final'
                ? 'You will move to the coding environment and cannot edit these steps afterwards.'
                : 'This will record your warm-up plan and load a fresh workspace for the full problem.'}
            </p>
            <div className="modal-actions">
              <button
                onClick={() => { logEvent('submit_modal_cancel'); setIsConfirmingSubmit(false); }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  executeSubmit();
                  setIsConfirmingSubmit(false);
                }}
                className="modal-confirm-btn"
              >
                {submitIntent === 'final' ? 'Open IDE' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </>
  );
}

const ThankYouPage = () => (
  <div className="thankyou-shell">
    <h1>Thank you!</h1>
    <p>Your submission and survey have been received.</p>

    <div className="thankyou-actions">
      <Link className="thankyou-link primary" to="/submission/task">
        View your submission - task
      </Link>
      <Link className="thankyou-link" to="/submission/code">
        View your submission - code
      </Link>
    </div>
  </div>
);

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // This will control the loading state

  useEffect(() => {
    // Re-send locally buffered logs (best-effort)
    function drainLogBuffer() {
      try {
        const raw = localStorage.getItem('rt_log_buffer');
        if (!raw) return;
        const buf = JSON.parse(raw);
        if (!Array.isArray(buf) || buf.length === 0) return;
  
        // Only try a chunk to avoid spamming
        const toSend = buf.slice(0, 50);
  
        toSend.forEach(e => {
          const body = JSON.stringify(e);
          if (navigator.sendBeacon) {
            // sendBeacon is fire-and-forget; same-origin cookies are sent automatically
            navigator.sendBeacon('/api/log', new Blob([body], { type: 'application/json' }));
          } else {
            // Fallback to fetch
            fetch('/api/log', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body
            }).catch(() => {/* keep in buffer on failure */});
          }
        });
      } catch {
        // ignore parse/storage errors
      }
    }
  
    // 1) Drain immediately on mount
    drainLogBuffer();
  
    // 2) Drain again when tab is moving to background (common time to flush)
    const onVis = () => {
      if (document.visibilityState === 'hidden') drainLogBuffer();
    };
    document.addEventListener('visibilitychange', onVis);
  
    // 3) Your existing session check
    fetch('/api/check_session')
      .then(res => res.json())
      .then(data => {
        if (data.logged_in) setIsLoggedIn(true);
      })
      .finally(() => setIsLoading(false));
  
    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
  };

  // --- ADD THIS BLOCK ---
  // While we're checking the session, show a simple loading message
  if (isLoading) {
    return <div>Loading application...</div>;
  }

  // After the check is done, render the correct routes
  return (
    <Router>
      <StartupRedirect isLoggedIn={isLoggedIn} />
      <PathPersistence />
      <Routes>
        <Route path="/login" element={!isLoggedIn ? <LoginPage /> : <Navigate to="/" />} />
        <Route path="/callback" element={<Callback onLoginSuccess={handleLoginSuccess} />} />
        <Route
  path="/"
  element={isLoggedIn ? <StepsHub /> : <Navigate to="/login" replace />}
/>
        <Route path="/ide" element={isLoggedIn ? <IdePage /> : <Navigate to="/login" />} />
        <Route path="/survey" element={isLoggedIn ? <SurveyPage /> : <Navigate to="/login" />} />
        <Route path="/post-survey" element={isLoggedIn ? <PostSurveyPage /> : <Navigate to="/login" />} />
        <Route path="/submission/task" element={isLoggedIn ? <SubmissionTaskPage /> : <Navigate to="/login" />} />
        <Route path="/submission/code" element={isLoggedIn ? <SubmissionCodePage /> : <Navigate to="/login" />} />
        <Route path="/thank-you" element={isLoggedIn ? <ThankYouPage /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}


export default App;
